import React, { forwardRef } from 'react';

import { useTheme } from '@mui/material';
import Typography, { TypographyProps } from '@mui/material/Typography';

import { getCascadeBreakpointValue } from '~components/ui/typography/utils/get-cascade-breakpoint-value';
import { getFluidFontSize } from '~components/ui/typography/utils/get-fluid-font-size';

import {
  Breakpoint,
  useCurrentBreakpoint,
} from '~hooks/use-current-breakpoint';

export type ResponsiveProp<T> = Partial<Record<Breakpoint, T>>;

export type AppTypographySize =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'h7'
  | 'h8'
  | 'body-lg'
  | 'body-md'
  | 'body-sm'
  | 'body-caption';

type AppFontWeight = 'regular' | 'semibold' | 'bold';

type AppTypographyProps = {
  size?: ResponsiveProp<AppTypographySize> | AppTypographySize;
  weight?: ResponsiveProp<AppFontWeight> | AppFontWeight;
  link?: boolean;
  fluid?: boolean;
} & TypographyProps;

const weightMapping: Record<AppFontWeight, number> = {
  regular: 400,
  semibold: 600,
  bold: 700,
};

// const breakpointsOrder: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];

export const AppTypography = forwardRef<HTMLDivElement, AppTypographyProps>(
  (
    {
      size = { xs: 'body-md' },
      weight = { xs: 'regular' },
      link = false,
      sx,
      children,
      fluid,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    const currentBreakpoint = useCurrentBreakpoint();
    const isSimpleSize = typeof size === 'string';
    const isSimpleWeight = typeof weight === 'string';

    const resolvedVariant = isSimpleSize
      ? size
      : (getCascadeBreakpointValue(
          size,
          currentBreakpoint
          // breakpointsOrder
        ) as TypographyProps['variant']);
    const resolvedWeight = isSimpleWeight
      ? weight
      : (getCascadeBreakpointValue(
          weight,
          currentBreakpoint
          // breakpointsOrder
        ) as AppFontWeight);

    const isHeading = resolvedVariant?.startsWith('h');

    const shouldUseClamp =
      fluid && !isSimpleSize && Object.keys(size).length === 2;

    const fontSizeClamp = shouldUseClamp
      ? getFluidFontSize(size as Record<Breakpoint, AppTypographySize>, theme)
      : undefined;

    return (
      <Typography
        {...rest}
        ref={ref}
        variant={resolvedVariant}
        component={'span'}
        sx={{
          ...(fontSizeClamp && { fontSize: `${fontSizeClamp} !important` }),
          ...{ fontWeight: weightMapping[resolvedWeight] },
          // ...(!isHeading && { fontWeight: weightMapping[resolvedWeight] }),
          ...(link && {
            textDecoration: 'underline',
            color: 'primaryTokens.100',
            cursor: 'pointer',

            '&:hover': {
              textDecoration: 'underline',
            },
          }),
          display: 'inline-block',
          ...sx,
        }}
      >
        {children}
      </Typography>
    );
  }
);
